import React, { useState } from 'react';

import { MenuItem, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import ColourPicker from './ColourPicker';
import { chartPaletteColours } from './colourPickerModel';
import { type SeriesOverride, seriesOverrides } from './seriesStyle';
import { Row } from './settingsKit';
import type { Block } from './types';
import type { BoardEditor } from './useBoardEdit';

export default function SeriesColour({
  block,
  name,
  editor,
  seriesLabel,
}: {
  block: Block;
  name: string;
  editor: BoardEditor;
  seriesLabel?: string;
}) {
  const theme = useTheme();
  const colours = chartPaletteColours(theme.palette.mode === 'dark');
  const current: SeriesOverride = seriesOverrides(block)[name] ?? {};
  return (
    <Row
      keywords={['color', 'colours', 'colors', 'palette', 'custom', 'hex', 'wheel', 'picker', seriesLabel ?? name]}
      label={seriesLabel ? `Colour · ${seriesLabel}` : 'Colour'}
      set={current.color !== undefined}
      stacked
    >
      <ColourPicker
        automaticColour={colours[0]}
        seriesColours={colours}
        disabled={editor.pending || editor.status === 'saving'}
        key={`${block.id}:${name}`}
        onApply={(colour) =>
          editor.apply({ op: 'set', id: block.id, patch: { series: { [name]: { color: colour } } } })
        }
        value={current.color}
      />
    </Row>
  );
}

export function SeriesColours({
  block,
  parts,
  editor,
}: {
  block: Block;
  parts: Array<[string, string]>;
  editor: BoardEditor;
}) {
  const [selected, setSelected] = useState(parts[0]?.[0]);
  const chosen = parts.find(([part]) => part === selected) ?? parts[0];
  if (!chosen) return null;
  const [part, label] = chosen;
  return (
    <>
      {parts.length > 1 ? (
        <Row keywords={['color', 'colour', 'palette', 'custom', 'hex', 'wheel', 'picker']} label="Series" stacked>
          <TextField
            fullWidth
            label="Choose a series"
            onChange={(event) => setSelected(event.target.value)}
            select
            size="small"
            value={part}
          >
            {parts.map(([id, name]) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </TextField>
        </Row>
      ) : null}
      <SeriesColour block={block} editor={editor} name={part.slice(part.indexOf(':') + 1)} seriesLabel={label} />
    </>
  );
}
